import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { countryOptions, DocumentParty, indicativeCurrencyOptions } from '@foundation/invoices/models';
import { TranslateDirective, TranslationService } from '@foundation/translations/services';

/** The fields, in the order a form asks for them. Declared once: the profile page and the invoice
 * editor are asking the same question, and a field added here appears in both. */
const FIELDS = [
	{ key: 'name', label: 'Name', type: 'text' },
	{ key: 'email', label: 'Email', type: 'email' },
	{ key: 'addressLine1', label: 'Address', type: 'text' },
	{ key: 'postalCode', label: 'Postal code', type: 'text' },
	{ key: 'city', label: 'City', type: 'text' },

	{ key: 'vatNumber', label: 'VAT number (EU businesses)', type: 'text' },
	// French only: they are INSEE identifiers, and the EU-wide one is the VAT number.
	{ key: 'siren', label: 'SIREN (France, 9 digits)', type: 'text' },
	{ key: 'siret', label: 'SIRET (France, 14 digits)', type: 'text' },
] as const;

/**
 * Who a document is addressed to: a name, an address, the country that decides the VAT treatment,
 * and the identifiers that route it.
 *
 * The same form on both sides of the transaction -- the customer fills it on their profile so it
 * never has to be retyped, and an admin corrects it on a draft when it is wrong or missing. Two
 * copies of these fields would be two places to add the next one to.
 */
@Component({
	selector: 'lib-billing-details-form',
	standalone: true,
	imports: [CommonModule, FormsModule, TranslateDirective],
	templateUrl: './billing-details-form.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingDetailsFormComponent {
	party = model.required<DocumentParty>();
	/** Shown above the fields when the caller wants to say why they are being asked. */
	explanation = input<string | null>(null);
	disabled = input(false);

	/** The currency documents are actually issued and paid in. Given by the caller rather than
	 * assumed here, and the second-currency question is only asked when it is: a shared lib does not
	 * know what any one app bills in. */
	baseCurrency = input<string | null>(null);
	/** A second currency to quote for information. Never an amount charged, and the form says so. */
	indicativeCurrency = model<string | null>(null);

	private _translationService = inject(TranslationService);

	protected readonly fields = FIELDS;
	protected readonly indicativeCurrencies = indicativeCurrencyOptions(this._translationService.currentLangCode$$$.value ?? 'en');
	/** A list, not a text box: an invoice carries the ISO 3166-1 alpha-2 code and nothing else, so
	 * "France" typed in good faith is a document our own rules refuse -- after the fact, and in the
	 * vocabulary of the rule. Named in the language the app is running in. */
	protected readonly countries = countryOptions(this._translationService.currentLangCode$$$.value ?? 'en');

	/** A party is worth billing when it says who and where: EN 16931 asks for the rest at issue. */
	isComplete = computed(() => {
		const party = this.party();
		return !!party.name?.trim() && !!party.country?.trim() && !!party.email?.trim();
	});

	valueOf(key: string) {
		return ((this.party() as unknown as Record<string, string | null | undefined>)[key] ?? '') as string;
	}

	setField(key: string, value: string) {
		this.party.update((party) => ({ ...party, [key]: value }));
	}
}
